import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerPanel } from '../ui/DrawerPanel';
import { updateCallScreening, getConfigurationBySenior } from '../../lib/supabase';
import { API_BASE_URL } from '../../config/apiUrl';
import { syncSeniorConfig } from '../../lib/syncConfig';

interface ScamBlockingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  /** Fired after a successful save so the dashboard tile can re-read state. */
  onUpdated?: () => void;
}

/**
 * D8 (2026-08-10) — scam-call blocking, lifted out of SettingsDrawer's "Call
 * protection" section into its own panel behind its own tile.
 *
 * It was buried inside "Update phone settings", two scrolls below a layout
 * picker, even though the FAQ's "you can turn it off anytime from your
 * dashboard" promise points at this switch. It is also the only safety toggle
 * on the dashboard, so it should not be a footnote inside a cosmetic panel.
 *
 * The logic is moved verbatim, not rewritten — same optimistic flip, same
 * rollback, same sync-config call, same unknown-state handling. In particular:
 * `callScreening` defaults to TRUE because that is the fail-safe for the device,
 * but a failed READ must not render as "protected", so a load failure shows an
 * explicit unknown state with a retry instead of a confident toggle.
 */
export const ScamBlockingDrawer: FC<ScamBlockingDrawerProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const [callScreening, setCallScreening] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!isOpen || !seniorId) return;
    let cancelled = false;
    setLoaded(false);
    setError(null);
    setLoadFailed(false);
    (async () => {
      try {
        const config = await getConfigurationBySenior(seniorId);
        if (cancelled) return;
        setCallScreening(config?.call_screening_enabled ?? true);
      } catch (err) {
        if (cancelled) return;
        console.error('[ScamBlocking] Failed to load call-screening state:', err);
        setError(t('drawers.settings.screeningLoadError'));
        setLoadFailed(true);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, seniorId, t, reloadKey]);

  const handleToggle = async () => {
    const next = !callScreening;
    setCallScreening(next); // optimistic
    setSaving(true);
    setError(null);
    try {
      await updateCallScreening(seniorId, next);
      // Auth + ownership now required server-side; syncSeniorConfig attaches
      // the JWT and never throws (see lib/syncConfig.ts).
      await syncSeniorConfig(seniorId);
      onUpdated?.();
    } catch (err) {
      console.error('[ScamBlocking] Failed to update call screening:', err);
      setCallScreening(!next); // roll back the optimistic flip
      setError(t('drawers.settings.screeningSaveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerPanel isOpen={isOpen} onClose={onClose} title={t('drawers.settings.callProtection')}>
      {!loaded ? (
        <div className="h-16 bg-brand-bg-warm rounded-xl animate-pulse" />
      ) : loadFailed ? (
        <div className="p-4 rounded-xl border border-amber-200 bg-amber-50">
          <p className="text-sm text-amber-900 flex items-start gap-2">
            <span className="material-symbols-outlined text-lg">help</span>
            {t('drawers.settings.screeningUnknown')}
          </p>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="mt-3 text-xs font-semibold text-brand-primary underline"
          >
            {t('drawers.settings.retry')}
          </button>
        </div>
      ) : (
        <>
          <label className="flex items-center justify-between gap-3 p-4 rounded-xl border border-brand-border">
            <span className="flex flex-col">
              <span className="text-sm font-semibold text-brand-text">
                {t('drawers.settings.scamBlocking')}
              </span>
              <span className="text-xs text-brand-text-muted">
                {t('drawers.settings.scamBlockingHint')}
              </span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={callScreening}
              aria-label={t('drawers.settings.scamBlocking')}
              disabled={saving}
              onClick={handleToggle}
              className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors disabled:opacity-60 ${
                callScreening ? 'bg-brand-success' : 'bg-brand-border'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  callScreening ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </label>
          {!callScreening && (
            <p className="mt-2 text-xs text-amber-700 flex items-start gap-1.5">
              <span className="material-symbols-outlined text-sm">warning</span>
              {t('drawers.settings.scamBlockingOffWarning', { name: seniorName })}
            </p>
          )}
          {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
        </>
      )}
    </DrawerPanel>
  );
};

export default ScamBlockingDrawer;
